import React from "react";
import withLayout from "../../../../hocs/Layout";
import { TabContext, TabPanel } from "@mui/lab";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MerchantLocations from "./B2BPickupLocation";
import {
  FireTruckOutlined,
  LocalShippingOutlined,

} from "@mui/icons-material";
import { decodeChannelAccess } from "../../../../helpers/UtilityHelper";
import PageTitle from "../../../../common/PageTitle";
import { isAdmin } from "../../../../helpers/ReduxHelpers";
import { pageContainerSx, tabContainerSx, tabPanelSx } from '../../../../styles/patterns';
import { NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, withOpacity } from '../../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../../styles/foundation/typography';

const PickupLocationTab = () => {
  const channelAccess = decodeChannelAccess();

  const getInitialTabValue = () => {
    if (channelAccess.PTL_ACCESS) return "1";
    if (channelAccess.B2C_ACCESS) return "2";
    return "1"; // fallback
  };

  const [value, setValue] = React.useState(getInitialTabValue());

  const hasAnyAccess = channelAccess.PTL_ACCESS || channelAccess.B2C_ACCESS || isAdmin();

  // Segmented tabs — same design as the Channel Orders / Profile tab strips.
  const tabs = [
    (channelAccess.PTL_ACCESS || isAdmin()) && {
      value: "1", label: "PTL Freight Pickup Locations", icon: <FireTruckOutlined />, color: "#283593",
    },
    (channelAccess.B2C_ACCESS || isAdmin()) && {
      value: "2", label: "D2C Freight Pickup Locations", icon: <LocalShippingOutlined />, color: PRIMARY_BLUE,
    },
  ].filter(Boolean);

  // Scroll arrows for the tab strip — shown only when there's more to scroll to on
  // that side, since the native scrollbar is hidden. Mirrors the Channel Orders tabs.
  const tabScrollRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const updateScrollArrows = React.useCallback(() => {
    const el = tabScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  React.useEffect(() => {
    updateScrollArrows();
    window.addEventListener('resize', updateScrollArrows);
    return () => window.removeEventListener('resize', updateScrollArrows);
  }, [updateScrollArrows]);

  const scrollTabsBy = (amount) => {
    tabScrollRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  if (!hasAnyAccess) {
    return (
      <Box sx={pageContainerSx}>
        <PageTitle>Pickup Locations</PageTitle>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          backgroundColor: "#fff",
          boxShadow: 1,
          borderRadius: 1
        }}>
          <Typography variant="h6" color="text.secondary">
            No access granted for B2B or PTL pickup locations
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={pageContainerSx}>
      <PageTitle>Pickup Locations</PageTitle>
      <TabContext value={value}>
        <Box sx={tabContainerSx}>
          {/* Segmented tab strip, mirrors the Channel Orders / Profile tabs */}
          <Box sx={{ bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', mb: 2, overflow: 'hidden', position: 'relative' }}>
            {canScrollLeft && (
              <IconButton onClick={() => scrollTabsBy(-220)} size="small"
                sx={{
                  position: 'absolute', left: 0, top: 0, bottom: 0, zIndex: 2, borderRadius: 0, px: 0.5, bgcolor: WHITE,
                  background: `linear-gradient(to right, ${WHITE} 60%, ${withOpacity(WHITE, 0)})`,
                  '&:hover': { bgcolor: 'transparent' },
                }}>
                <ChevronLeftIcon sx={{ color: SUBTLE }} />
              </IconButton>
            )}
            {canScrollRight && (
              <IconButton onClick={() => scrollTabsBy(220)} size="small"
                sx={{
                  position: 'absolute', right: 0, top: 0, bottom: 0, zIndex: 2, borderRadius: 0, px: 0.5, bgcolor: WHITE,
                  background: `linear-gradient(to left, ${WHITE} 60%, ${withOpacity(WHITE, 0)})`,
                  '&:hover': { bgcolor: 'transparent' },
                }}>
                <ChevronRightIcon sx={{ color: SUBTLE }} />
              </IconButton>
            )}
            <Box ref={tabScrollRef} onScroll={updateScrollArrows}
              sx={{
                display: 'flex', overflowX: 'auto',
                scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { display: 'none' },
              }}>
              {tabs.map((t, i) => {
                const isActive = value === t.value;
                return (
                  <React.Fragment key={t.value}>
                    {i > 0 && <Divider orientation="vertical" flexItem sx={{ borderColor: BORDER }} />}
                    <Box onClick={() => setValue(t.value)}
                      sx={{
                        flex: '1 1 auto', minWidth: 170, py: 1.25, px: 2, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                        borderBottom: isActive ? `3px solid ${t.color}` : '3px solid transparent',
                        bgcolor: isActive ? withOpacity(t.color, 0.06) : 'transparent',
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: withOpacity(t.color, 0.04) },
                      }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
                        bgcolor: isActive ? withOpacity(t.color, 0.12) : withOpacity(t.color, 0.07),
                        color: isActive ? t.color : SUBTLE,
                        '& .MuiSvgIcon-root': { fontSize: 17 },
                      }}>
                        {t.icon}
                      </Box>
                      <Typography sx={{
                        fontFamily: FONT, fontSize: SIZE.xs, fontWeight: isActive ? WEIGHT.bold : WEIGHT.medium,
                        color: isActive ? NAVY : SUBTLE, lineHeight: 1.2, whiteSpace: 'nowrap',
                      }}>
                        {t.label}
                      </Typography>
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        </Box>
        <TabPanel value="1" sx={tabPanelSx}>
          <MerchantLocations shipmentType="B2B" />
        </TabPanel>
        <TabPanel value="2" sx={tabPanelSx}>
          <MerchantLocations shipmentType="B2C" />
        </TabPanel>
      </TabContext>
    </Box>
  )
}

export default withLayout(PickupLocationTab)
